import React, { useState, useMemo } from "react";
import { Plus, X, Search, Microscope } from "lucide-react";

/**
 * DiagnosticaPicker — menu chiuso di default. Si apre solo quando il medico vuole aggiungere
 * un esame. Supporta ricerca, filtro per categoria, e mostra i preferiti del modello in alto.
 *
 * Props:
 *  - options: Array<{id, label}> dall'intera library DIAGNOSTICA_OPTIONS
 *  - selected: Array<id>
 *  - favorites: Array<id> dei suggeriti dal modello/medico
 *  - onAdd(id), onClose: callback
 */
const CATEGORIES = {
  retina: { label: "Retina", ids: ["oct_macula", "angio_oct", "retinografia", "fluorangiografia", "ecografia", "icga"] },
  glaucoma: { label: "Glaucoma", ids: ["oct_rnfl", "campo_visivo", "pachimetria", "gonioscopia", "oct_segmento_anteriore", "curva_tonometrica"] },
  cornea: { label: "Cornea / Refrattiva", ids: ["topografia_corneale", "tomografia_corneale", "pachimetria", "microscopia_endoteliale", "aberrometria", "pupillometria", "schirmer_but"] },
  cataratta: { label: "Cataratta", ids: ["biometria", "topografia_corneale", "oct_macula", "microscopia_endoteliale"] },
  ortottica: { label: "Ortottica / Motilità", ids: ["diag_hess_lancaster", "diag_gaze9_foto", "luci_worth", "stereopsi", "cover_test"] },
  laser: { label: "Laser / Prestazioni", ids: ["yag_capsulotomia", "slt_argon_micropulsato", "laser_argon", "laser_yag", "laser_slt"] },
  altri: { label: "Altri", ids: ["amsler", "allegati"] },
};

export default function DiagnosticaPicker({ options, selected, favorites, onAdd, onClose }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("preferiti");
  const selSet = useMemo(() => new Set(selected || []), [selected]);
  const favSet = useMemo(() => new Set(favorites || []), [favorites]);
  // Filtra opzioni
  const filtered = useMemo(() => {
    let list = options;
    if (cat === "preferiti") {
      list = list.filter((o) => favSet.has(o.id));
    } else if (cat !== "tutti") {
      const ids = new Set(CATEGORIES[cat]?.ids || []);
      list = list.filter((o) => ids.has(o.id));
    }
    if (q.trim()) {
      const qq = q.toLowerCase();
      list = list.filter((o) => (o.label || "").toLowerCase().includes(qq) || (o.id || "").toLowerCase().includes(qq));
    }
    return list;
  }, [options, q, cat, favSet]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="diag-picker-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
          <Microscope size={18} className="text-brand-azure" />
          <h3 className="font-extrabold text-brand-navy text-lg flex-1">Aggiungi diagnostica</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-brand-navy"><X size={18} /></button>
        </div>
        <div className="px-5 py-3 flex flex-col gap-2 border-b border-[#E2E8F0]">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              data-testid="diag-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca esame (es. OCT, biometria, campo visivo)…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[["preferiti", `★ Preferiti (${favSet.size})`], ["tutti", "Tutti"], ...Object.entries(CATEGORIES).map(([k, v]) => [k, v.label])].map(([k, lbl]) => (
              <button
                key={k}
                data-testid={`diag-cat-${k}`}
                onClick={() => setCat(k)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition ${cat === k ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure"}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="text-center text-slate-500 text-sm italic py-8">
              {cat === "preferiti" && favSet.size === 0
                ? "Nessun esame preferito configurato. Vai in Impostazioni → Modelli cartella per configurare i preferiti del modello."
                : "Nessun esame trovato. Prova un'altra ricerca o cambia categoria."}
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((o) => {
                const already = selSet.has(o.id);
                const isFav = favSet.has(o.id);
                return (
                  <li key={o.id}>
                    <button
                      disabled={already}
                      data-testid={`diag-pick-${o.id}`}
                      onClick={() => { onAdd(o.id); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition ${already ? "bg-emerald-50 text-emerald-700 cursor-default" : "bg-white hover:bg-brand-azure/5 text-slate-700"}`}
                    >
                      {isFav && <span className="text-amber-500 text-sm">★</span>}
                      <span className="flex-1">{o.label}</span>
                      {already ? (
                        <span className="text-[10px] font-bold uppercase bg-emerald-200 text-emerald-900 px-2 py-0.5 rounded">Già aggiunto</span>
                      ) : (
                        <Plus size={14} className="text-brand-azure" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
